import { createContext, useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Inbox, Users, Megaphone, CalendarDays, Building2, Home, Globe, LogOut, Menu, X, ChevronDown, Bell, GraduationCap, CheckCheck, Dumbbell } from "lucide-react";
import { authApi } from "@/lib/api";

export const ROLE_LABELS = {
  admin: "Administrador",
  responsavel: "Responsável Técnico",
  lideranca: "Liderança Estratégica",
  instrutor: "Instrutor",
  financeiro: "Financeiro",
  aluno: "Aluno",
};

const AppUserContext = createContext(null);
export const useAppUser = () => useContext(AppUserContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const load = async () => {
      if (localStorage.getItem("bonsai_token")) {
        try {
          const me = await authApi().get("/auth/me");
          setUser(me.data);
        } catch {
          localStorage.removeItem("bonsai_token");
          localStorage.removeItem("bonsai_user");
          setUser(null);
        }
      } else {
        setUser(null);
      }
      setReady(true);
    };
    load();
  }, [location.pathname]);

  return <AppUserContext.Provider value={{ user, ready }}>{children}</AppUserContext.Provider>;
}

const ADMIN_MENU = [
  { to: "/admin", key: "mensagens", label: "Mensagens", icon: Inbox, testid: "menu-mensagens" },
  {
    key: "pessoas", label: "Pessoas", icon: Users, testid: "menu-pessoas",
    children: [
      { to: "/admin/usuarios", label: "Todos os Usuários", testid: "submenu-usuarios-lista" },
      { to: "/admin/usuarios?novo=1", label: "Novo Usuário", testid: "submenu-usuarios-novo" },
      { to: "/carga-massiva", label: "Carga Massiva", testid: "submenu-usuarios-carga" },
      { to: "/validacoes", label: "Validações", testid: "submenu-validacoes" },
      { to: "/certificados", label: "Certificados", testid: "submenu-certificados" },
    ],
  },
  {
    key: "comunicacao", label: "Comunicação", icon: Megaphone, testid: "menu-comunicacao",
    children: [
      { to: "/admin/avisos", label: "Todos os Avisos", testid: "submenu-avisos-lista" },
      { to: "/admin/avisos?novo=1", label: "Novo Aviso", testid: "submenu-avisos-novo" },
      { to: "/eventos", label: "Todos os Eventos", testid: "submenu-eventos-lista" },
      { to: "/eventos?novo=1", label: "Novo Evento", testid: "submenu-eventos-novo" },
    ],
  },
  {
    key: "treinos", label: "Treinos", icon: Dumbbell, testid: "menu-treinos",
    children: [
      { to: "/presenca", label: "Presença", testid: "submenu-presenca" },
      { to: "/frequencia", label: "Frequência", testid: "submenu-frequencia" },
      { to: "/galeria", label: "Galeria", testid: "submenu-galeria" },
      { to: "/placar", label: "Placar", testid: "submenu-placar" },
      { to: "/cronometro", label: "Cronômetro", testid: "submenu-cronometro" },
      { to: "/plano-aula", label: "Plano de Aula", testid: "submenu-plano-aula" },
    ],
  },
  {
    key: "unidades", label: "Unidades", icon: Building2, testid: "menu-unidades",
    children: [
      { to: "/unidades", label: "Todas as Unidades", testid: "submenu-unidades-lista" },
      { to: "/unidades?novo=1", label: "Nova Unidade", testid: "submenu-unidades-nova" },
    ],
  },
];

const buildUserMenu = (isStaff, onlyAluno, canManageUsers, canSeeFrequencia) => [
  { to: "/painel", key: "minha-area", label: "Minha Área", icon: Home, testid: "menu-minha-area" },
  ...(onlyAluno
    ? [
        {
          key: "aluno", label: "Aluno", icon: GraduationCap, testid: "menu-aluno",
          children: [
            { to: "/aluno/dados", label: "Meus Dados", testid: "submenu-aluno-dados" },
            { to: "/aluno/pendencias", label: "Pendências", testid: "submenu-aluno-pendencias" },
          ],
        },
      ]
    : []),
  ...(isStaff
    ? [
        {
          key: "pessoas", label: "Pessoas", icon: Users, testid: "menu-pessoas",
          children: [
            ...(canManageUsers
              ? [
                  { to: "/admin/usuarios", label: "Todos os Usuários", testid: "submenu-usuarios-lista" },
                  { to: "/admin/usuarios?novo=1", label: "Novo Usuário", testid: "submenu-usuarios-novo" },
                  { to: "/carga-massiva", label: "Carga Massiva", testid: "submenu-usuarios-carga" },
                ]
              : []),
            { to: "/validacoes", label: "Validações", testid: "submenu-validacoes" },
            { to: "/certificados", label: "Certificados", testid: "submenu-certificados" },
          ],
        },
        {
          key: "treinos", label: "Treinos", icon: Dumbbell, testid: "menu-treinos",
          children: [
            { to: "/presenca", label: "Presença", testid: "submenu-presenca" },
            ...(canSeeFrequencia ? [{ to: "/frequencia", label: "Frequência", testid: "submenu-frequencia" }] : []),
            { to: "/galeria", label: "Galeria", testid: "submenu-galeria" },
            { to: "/placar", label: "Placar", testid: "submenu-placar" },
            { to: "/cronometro", label: "Cronômetro", testid: "submenu-cronometro" },
            { to: "/plano-aula", label: "Plano de Aula", testid: "submenu-plano-aula" },
          ],
        },
      ]
    : []),
  isStaff
    ? {
        key: "eventos", label: "Eventos", icon: CalendarDays, testid: "menu-eventos",
        children: [
          { to: "/eventos", label: "Todos os Eventos", testid: "submenu-eventos-lista" },
          { to: "/eventos?novo=1", label: "Novo Evento", testid: "submenu-eventos-novo" },
        ],
      }
    : { to: "/eventos", key: "eventos", label: "Eventos", icon: CalendarDays, testid: "menu-eventos" },
  { to: "/unidades", key: "unidades", label: "Unidades", icon: Building2, testid: "menu-unidades" },
];

export default function AppShell({ active, requireAdmin = false, requireRoles = null, children }) {
  const ctx = useAppUser() || {};
  const user = ctx.user;
  const ready = ctx.ready;
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(active);
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname + location.search;

  useEffect(() => {
    if (!ready) return;
    if (!user && !localStorage.getItem("bonsai_token")) {
      navigate("/login");
      return;
    }
    if (user && requireAdmin && !(user.roles || [user.role]).includes("admin")) {
      navigate("/painel");
    }
    if (user && requireRoles && !requireRoles.some((r) => (user.roles || [user.role]).includes(r))) {
      navigate("/painel");
    }
  }, [ready, user, requireAdmin, requireRoles, navigate]);

  const groupHasCurrent = (item) =>
    item.children?.some((s) => currentPath === s.to || location.pathname === s.to.split("?")[0]);

  useEffect(() => {
    const g = menu.find((i) => i.children && groupHasCurrent(i));
    if (g) setExpanded(g.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search, user]);

  const logout = () => {
    localStorage.removeItem("bonsai_token");
    localStorage.removeItem("bonsai_user");
    navigate("/login");
  };

  const roles = user ? user.roles || [user.role] : [];
  const isAdmin = roles.includes("admin");
  const isStaff = roles.some((r) => r !== "aluno");
  const onlyAluno = roles.length === 1 && roles[0] === "aluno";
  const menu = isAdmin ? ADMIN_MENU : buildUserMenu(isStaff, onlyAluno, roles.includes("responsavel"), roles.some((r) => ["responsavel", "instrutor"].includes(r)));

  const [notifs, setNotifs] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    if (!user) {
      setNotifs(null);
      return;
    }
    authApi()
      .get("/notifications")
      .then((r) => setNotifs(r.data))
      .catch(() => {});
  }, [user, location.pathname]);

  const unread = notifs?.unread || 0;

  const markAllRead = async () => {
    try {
      await authApi().patch("/notifications/read-all");
      setNotifs((n) => n && { ...n, unread: 0, notifications: n.notifications.map((x) => ({ ...x, read: true })) });
    } catch {}
  };

  const bellButton = (
    <button
      type="button"
      onClick={() => setPanelOpen(true)}
      data-testid="notifications-bell"
      className="relative w-9 h-9 rounded-lg border border-white/15 flex items-center justify-center text-zinc-400 hover:text-gold hover:border-gold/50 transition-colors"
      aria-label="Notificações"
    >
      <Bell size={16} />
      {unread > 0 && (
        <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-crimson text-white text-[10px] font-mono flex items-center justify-center" data-testid="notifications-badge">
          {unread}
        </span>
      )}
    </button>
  );

  const sidebarContent = (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-5 h-20 border-b border-white/10 shrink-0">
        <img src="/assets/logo.jpeg" alt="Projeto Bonsai BJJ" className="w-10 h-10 rounded-full object-cover ring-1 ring-gold/50" />
        <div className="flex-1">
          <p className="font-display font-bold text-sm leading-tight">PROJETO <span className="text-gold">BONSAI</span></p>
          <p className="font-mono text-[10px] tracking-widest text-zinc-500">ÁREA LOGADA</p>
        </div>
        {bellButton}
      </div>
      <nav className="flex-1 px-3 py-5 space-y-1.5 overflow-y-auto" data-testid="sidebar-menu">
        {menu.map((item) =>
          item.children ? (
            <div key={item.key}>
              <button
                data-testid={item.testid}
                onClick={() => setExpanded(expanded === item.key ? null : item.key)}
                className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl font-display text-sm transition-all duration-200 border ${
                  active === item.key || groupHasCurrent(item)
                    ? "bg-gold/15 text-gold border-gold/30"
                    : "text-zinc-400 hover:text-gold hover:bg-white/5 border-transparent"
                }`}
              >
                <span className="flex items-center gap-3">
                  <item.icon size={18} /> {item.label}
                </span>
                <ChevronDown size={16} className={`transition-transform duration-300 ${expanded === item.key ? "rotate-180" : ""}`} />
              </button>
              {expanded === item.key && (
                <div className="ml-6 mt-1 mb-1 pl-4 border-l border-white/10 space-y-1" data-testid={`${item.testid}-submenu`}>
                  {item.children.map((sub) => (
                    <Link
                      key={sub.to}
                      to={sub.to}
                      data-testid={sub.testid}
                      onClick={() => setOpen(false)}
                      className={`block px-3 py-2 rounded-lg text-sm transition-colors ${
                        currentPath === sub.to ? "text-gold bg-gold/10" : "text-zinc-500 hover:text-gold"
                      }`}
                    >
                      {sub.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <Link
              key={item.key}
              to={item.to}
              data-testid={item.testid}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 px-4 py-3 rounded-xl font-display text-sm transition-all duration-200 border ${
                active === item.key
                  ? "bg-gold/15 text-gold border-gold/30"
                  : "text-zinc-400 hover:text-gold hover:bg-white/5 border-transparent"
              }`}
            >
              <item.icon size={18} /> {item.label}
            </Link>
          )
        )}
      </nav>
      <div className="border-t border-white/10 p-4 space-y-3 shrink-0">
        {user && (
          <div data-testid="sidebar-user-info">
            <p className="font-display font-semibold text-sm truncate">{user.name}</p>
            <p className="font-mono text-[10px] tracking-wider text-zinc-500 truncate">
              {roles.map((r) => ROLE_LABELS[r] || r).join(" + ").toUpperCase()}
            </p>
          </div>
        )}
        <div className="flex gap-2">
          <Link to="/" data-testid="sidebar-site-link" className="flex-1 inline-flex items-center justify-center gap-2 font-mono text-[11px] tracking-widest border border-white/15 text-zinc-300 px-3 py-2.5 rounded-lg hover:border-gold/50 hover:text-gold transition-colors">
            <Globe size={13} /> SITE
          </Link>
          <a href="/downloads/manual_de_uso_bonsai_bjj.pdf" target="_blank" rel="noreferrer" data-testid="sidebar-manual-link" className="flex-1 inline-flex items-center justify-center gap-2 font-mono text-[11px] tracking-widest border border-white/15 text-zinc-300 px-3 py-2.5 rounded-lg hover:border-gold/50 hover:text-gold transition-colors">
            MANUAL
          </a>
          <button onClick={logout} data-testid="sidebar-logout-button" className="flex-1 inline-flex items-center justify-center gap-2 font-mono text-[11px] tracking-widest border border-crimson/50 text-crimson px-3 py-2.5 rounded-lg hover:bg-crimson hover:text-white transition-colors">
            <LogOut size={13} /> SAIR
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-obsidian" data-testid="app-shell">
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 h-16 bg-obsidian/90 backdrop-blur-xl border-b border-white/10 flex items-center justify-between px-5">
        <div className="flex items-center gap-3">
          <img src="/assets/logo.jpeg" alt="" className="w-8 h-8 rounded-full object-cover ring-1 ring-gold/50" />
          <span className="font-display font-bold text-sm">PROJETO <span className="text-gold">BONSAI</span></span>
        </div>
        <div className="flex items-center gap-2">
          {bellButton}
          <button onClick={() => setOpen(!open)} data-testid="sidebar-mobile-toggle" className="text-zinc-300 hover:text-gold transition-colors" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 bg-[#0D0D10] border-r border-white/10 z-40" data-testid="sidebar">
        {sidebarContent}
      </aside>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50" data-testid="sidebar-drawer">
          <div className="absolute inset-0 bg-black/70" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-72 bg-[#0D0D10] border-r border-white/10">{sidebarContent}</aside>
        </div>
      )}

      <main className="lg:pl-64 pt-16 lg:pt-0 min-h-screen">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-8 lg:py-10">{children}</div>
      </main>

      {panelOpen && (
        <div className="fixed inset-0 z-[60]" data-testid="notifications-panel">
          <div className="absolute inset-0 bg-black/60" onClick={() => setPanelOpen(false)} />
          <aside className="absolute inset-y-0 right-0 w-full max-w-sm bg-[#0D0D10] border-l border-white/10 flex flex-col">
            <div className="flex items-center justify-between px-6 h-20 border-b border-white/10 shrink-0">
              <p className="font-display font-bold">Notificações</p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={markAllRead}
                  data-testid="notifications-read-all"
                  className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-widest text-zinc-400 hover:text-gold transition-colors"
                >
                  <CheckCheck size={13} /> MARCAR LIDAS
                </button>
                <button type="button" onClick={() => setPanelOpen(false)} data-testid="notifications-close" className="text-zinc-400 hover:text-gold transition-colors" aria-label="Fechar notificações">
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {notifs === null ? (
                <p className="text-zinc-500 text-sm">Carregando...</p>
              ) : notifs.notifications.length === 0 ? (
                <div className="flex flex-col items-center gap-3 border border-dashed border-white/15 rounded-2xl py-14 text-zinc-500" data-testid="notifications-empty">
                  <Bell size={26} />
                  <p className="text-sm">Nenhuma notificação por aqui.</p>
                </div>
              ) : (
                notifs.notifications.map((n, i) => (
                  <div
                    key={n.id}
                    data-testid={`notification-item-${i}`}
                    className={`rounded-xl border px-4 py-3.5 ${n.read ? "border-white/10 bg-white/[0.02]" : "border-gold/40 bg-gold/5"}`}
                  >
                    <div className="flex items-start gap-2">
                      {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-gold shrink-0" data-testid={`notification-unread-${i}`} />}
                      <div className="min-w-0">
                        <p className="font-display font-semibold text-sm">{n.title}</p>
                        <p className="text-xs text-zinc-400 mt-1 leading-relaxed">{n.message}</p>
                        <p className="font-mono text-[10px] text-zinc-600 mt-2">
                          {new Date(n.created_at).toLocaleDateString("pt-BR")} {new Date(n.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
